import { ref, onScopeDispose } from 'vue';

/**
 * Reactive pointer-capability flags derived from the device's INPUT type rather than viewport
 * width. Width tells you how big the screen is; these tell you how the user actually interacts —
 * the right signal for "should this behave like a touch/mobile device or a precise desktop one."
 *
 *   isTouchPrimary   — primary pointer is touch (phones, tablets): (pointer: coarse)
 *   isDesktopPointer — a precise pointer with hover (mouse/trackpad): (pointer: fine) and (hover: hover)
 *
 * Both are reactive (update if the user attaches/detaches a mouse) and self-clean via the calling
 * component's effect scope (onScopeDispose).
 */
export function useDeviceCapability() {
  const isTouchPrimary = ref(false);
  const isDesktopPointer = ref(false);

  const cleanups = [];
  const bind = (mediaText, target) => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(mediaText);
    target.value = mql.matches;
    const handler = (e) => { target.value = e.matches; };
    mql.addEventListener('change', handler);
    cleanups.push(() => mql.removeEventListener('change', handler));
  };

  bind('(pointer: coarse)', isTouchPrimary);
  bind('(pointer: fine) and (hover: hover)', isDesktopPointer);

  onScopeDispose(() => cleanups.forEach((off) => off()));

  return { isTouchPrimary, isDesktopPointer };
}
